import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function stripOuterCodeFence(md = "") {
  const text = String(md ?? "")
    .replace(/\r\n/g, "\n")
    .trim();
  const fence = text.match(/^```(?:markdown|md|text)?\s*\n([\s\S]*?)\n```$/i);
  return fence ? fence[1].trim() : text;
}

function repairDanglingBullets(md = "") {
  const lines = String(md ?? "")
    .replace(/\r\n/g, "\n")
    .split("\n");
  const out = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Convert standalone "•" into a real list marker
    if (/^\s*•\s*$/.test(line)) {
      out.push("-");
      continue;
    }

    // Merge a dangling "-" "*" "+" with the next non-empty line
    if (/^\s*([-*+])\s*$/.test(line)) {
      let j = i + 1;
      while (j < lines.length && lines[j].trim() === "") j++;

      if (j < lines.length) {
        out.push(`${line.trim()} ${lines[j].trim()}`);
        i = j;
        continue;
      }
    }

    out.push(line);
  }

  return out.join("\n");
}

export const CopyableOutputBlock = ({
  title,
  description,
  content,
  educationalCallout,
}) => {
  const [copied, setCopied] = useState(false);

  const cleanedContent = useMemo(() => {
    let md = stripOuterCodeFence(content);

    // If the model emits literal "• " at the start of a line, normalize to "-"
    md = md.replace(/^\s*•\s+/gm, "- ");

    md = repairDanglingBullets(md);

    return md.trim();
  }, [content]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cleanedContent);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
          <div className="flex-1">
            <CardTitle className="text-lg sm:text-2xl">{title}</CardTitle>
            {description && (
              <CardDescription className="text-xs sm:text-sm">
                {description}
              </CardDescription>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="w-full sm:w-auto shrink-0"
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-3 sm:p-6 pt-0">
        <div className="bg-muted/50 p-3 sm:p-4 rounded-lg border overflow-x-auto">
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: (props) => (
                  <h1
                    className="text-xl font-bold text-foreground mb-3 mt-4"
                    {...props}
                  />
                ),
                h2: (props) => (
                  <h2
                    className="text-lg font-semibold text-foreground mb-2 mt-4"
                    {...props}
                  />
                ),
                h3: (props) => (
                  <h3
                    className="text-base font-semibold text-foreground mb-2 mt-3"
                    {...props}
                  />
                ),
                p: (props) => (
                  <p
                    className="text-xs sm:text-sm leading-relaxed text-foreground mb-3"
                    {...props}
                  />
                ),

                // KEY CHANGE: list-outside + padding for clean wrapping
                ul: (props) => (
                  <ul
                    className="list-disc list-outside pl-5 space-y-2 text-xs sm:text-sm text-foreground mb-3"
                    {...props}
                  />
                ),
                ol: (props) => (
                  <ol
                    className="list-decimal list-outside pl-5 space-y-2 text-xs sm:text-sm text-foreground mb-3"
                    {...props}
                  />
                ),
                li: (props) => <li className="leading-relaxed" {...props} />,

                strong: (props) => (
                  <strong
                    className="font-semibold text-foreground"
                    {...props}
                  />
                ),
                em: (props) => (
                  <em className="italic text-foreground" {...props} />
                ),

                pre: (props) => (
                  <pre
                    className="bg-muted/60 p-3 rounded-md overflow-x-auto mb-3"
                    {...props}
                  />
                ),
                code: ({ inline, children, ...props }) => {
                  if (inline) {
                    return (
                      <code
                        className="bg-muted/60 px-1.5 py-0.5 rounded text-[11px] sm:text-xs font-mono text-foreground"
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  }
                  return (
                    <code
                      className="text-[11px] sm:text-xs font-mono text-foreground"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },

                blockquote: (props) => (
                  <blockquote
                    className="border-l-4 border-primary pl-4 italic text-muted-foreground mb-3"
                    {...props}
                  />
                ),
                a: (props) => (
                  <a
                    className="text-primary hover:underline font-medium"
                    target="_blank"
                    rel="noreferrer"
                    {...props}
                  />
                ),
              }}
            >
              {cleanedContent}
            </ReactMarkdown>
          </div>
        </div>

        {educationalCallout && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-foreground leading-relaxed">
              {educationalCallout}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
